import { useState } from 'react';
import { X, Smartphone, ChevronDown, ChevronUp, Download } from 'lucide-react';

type Platform = 'ios-webview' | 'android-webview' | 'ios-safari' | 'android-chrome' | 'desktop';

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  // Detect in-app browsers: Instagram, Facebook, TikTok, Snapchat, Twitter, Line, etc.
  const isInAppBrowser =
    /Instagram|FBAN|FBAV|FB_IAB|Twitter|Line|Snapchat|TikTok|musical_ly/i.test(ua) ||
    (/wv\b/i.test(ua) && /Android/i.test(ua)); // generic Android WebView flag
  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPad Pro
  const isAndroid = /Android/i.test(ua);

  if (isInAppBrowser && isIOS) return 'ios-webview';
  if (isInAppBrowser) return 'android-webview';
  if (isIOS) return 'ios-safari';
  if (isAndroid) return 'android-chrome';
  return 'desktop';
}

const PLATFORM_INFO: Record<Platform, { title: string; subtitle: string; steps: string[] }> = {
  'ios-webview': {
    title: 'Open in Safari to Install',
    subtitle: "Instagram's browser doesn't support app installation. Open in Safari first:",
    steps: [
      'Tap ••• at the bottom-right of the screen',
      'Select "Open in Safari" or "Open in External Browser"',
      'In Safari, tap the Share icon (↑□) at the bottom bar',
      'Scroll down and tap "Add to Home Screen"',
      'Tap "Add" in the top-right — done! 🎉',
    ],
  },
  'android-webview': {
    title: 'Open in Chrome to Install',
    subtitle: "This in-app browser doesn't support installation. Open in Chrome first:",
    steps: [
      'Tap ⋮ (three dots) at the top-right of the screen',
      'Select "Open in Chrome" or "Open in external browser"',
      'In Chrome, tap ⋮ at the top right',
      'Tap "Add to Home Screen" or "Install App"',
      'Tap "Add" to confirm — done! 🎉',
    ],
  },
  'ios-safari': {
    title: 'Add to Home Screen',
    subtitle: 'Install Mart-24 as a native app on your iPhone or iPad:',
    steps: [
      'Tap the Share icon (↑□) at the bottom of Safari',
      'Scroll down in the share menu',
      'Tap "Add to Home Screen"',
      'Tap "Add" in the top-right corner — done! 🎉',
    ],
  },
  'android-chrome': {
    title: 'Install the App',
    subtitle: 'Add Mart-24 to your home screen for the best experience:',
    steps: [
      'Tap ⋮ at the top-right corner of Chrome',
      'Tap "Add to Home Screen" or "Install App"',
      'Tap "Add" to confirm — done! 🎉',
    ],
  },
  'desktop': {
    title: 'Install the App',
    subtitle: 'Install Mart-24 for quick desktop access:',
    steps: [
      'Click the install icon ⊕ in the browser address bar',
      'Click "Install" to confirm',
    ],
  },
};

interface Props {
  deferredPrompt: any;
  onNativeInstall: () => Promise<boolean>;
  onDismiss: () => void;
}

export default function AppInstallBanner({ deferredPrompt, onNativeInstall, onDismiss }: Props) {
  const [showSteps, setShowSteps] = useState(false);
  const [installing, setInstalling] = useState(false);

  const platform = detectPlatform();
  // Native install prompt only works in Android Chrome / Desktop with a deferred prompt
  const canUseNativePrompt = (platform === 'android-chrome' || platform === 'desktop') && !!deferredPrompt;
  const info = PLATFORM_INFO[platform];

  const handleInstallClick = async () => {
    if (canUseNativePrompt) {
      setInstalling(true);
      await onNativeInstall();
      setInstalling(false);
    } else {
      setShowSteps(prev => !prev);
    }
  };

  return (
    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm">
      {/* Compact banner row */}
      <div className="flex items-center justify-between gap-3 px-4 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <Smartphone className="w-4 h-4 flex-shrink-0 text-indigo-200" />
          <p className="text-sm font-medium truncate">
            Install Mart-24 App for order alerts &amp; faster access
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleInstallClick}
            disabled={installing}
            className="flex items-center gap-1 text-xs font-semibold bg-white text-indigo-700 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
          >
            {canUseNativePrompt ? (
              <>
                <Download className="w-3 h-3" />
                {installing ? 'Installing…' : 'Install Now'}
              </>
            ) : (
              <>
                {showSteps ? 'Hide Steps' : 'How to Install'}
                {showSteps ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </>
            )}
          </button>
          <button
            onClick={onDismiss}
            aria-label="Dismiss install banner"
            className="text-indigo-200 hover:text-white transition-colors p-0.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Expanded steps panel */}
      {showSteps && (
        <div className="px-4 pb-4 bg-indigo-800/40">
          <p className="text-sm font-semibold text-white mb-0.5">{info.title}</p>
          <p className="text-xs text-indigo-200 mb-3">{info.subtitle}</p>
          <ol className="space-y-2.5">
            {info.steps.map((step, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center text-xs font-bold text-white">
                  {i + 1}
                </span>
                <span className="text-sm text-indigo-100 leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
